import React, { useState, useEffect } from 'react';
import { discountCodesAPI } from '../../services/api';
import '../admin/AdminComponents.css';

const DiscountCodeForm = ({ code, onClose }) => {
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    minPurchaseAmount: '',
    maxDiscountAmount: '',
    validFrom: '',
    validUntil: '',
    maxUseCount: '',
    active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (code) {
      setFormData({
        code: code.code || '',
        description: code.description || '',
        discountType: code.discount_type || 'percentage',
        discountValue: code.discount_value || '',
        minPurchaseAmount: code.min_purchase_amount || '',
        maxDiscountAmount: code.max_discount_amount || '',
        validFrom: code.valid_from ? new Date(code.valid_from).toISOString().slice(0, 16) : '',
        validUntil: code.valid_until ? new Date(code.valid_until).toISOString().slice(0, 16) : '',
        maxUseCount: code.max_use_count || '',
        active: code.active !== undefined ? code.active : true
      });
    }
  }, [code]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = {
        code: formData.code.trim().toUpperCase(),
        description: formData.description.trim() || null,
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        minPurchaseAmount: formData.minPurchaseAmount ? parseFloat(formData.minPurchaseAmount) : 0,
        maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
        validFrom: formData.validFrom || null,
        validUntil: formData.validUntil || null,
        maxUseCount: formData.maxUseCount ? parseInt(formData.maxUseCount) : null,
        active: formData.active
      };

      if (code) {
        await discountCodesAPI.update(code.id, data);
      } else {
        await discountCodesAPI.create(data);
      }

      onClose();
    } catch (error) {
      setError(error.message || 'Failed to save discount code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>{code ? 'Edit Discount Code' : 'Add Discount Code'}</h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>

        </div>
        <form onSubmit={handleSubmit} className="admin-form">
          {error && (
            <div className="admin-error-message">
              {error}
            </div>
          )}
          <div className="admin-form-group">
            <label className="admin-form-label">
              Code <span className="admin-required">*</span>
            </label>
            <input
              type="text"
              name="code"
              className="admin-form-input"
              value={formData.code}
              onChange={handleChange}
              required
              placeholder="SAVE10"
              style={{ textTransform: 'uppercase' }}
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Description</label>
            <textarea
              name="description"
              className="admin-form-textarea"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Optional description"
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">
              Discount Type <span className="admin-required">*</span>
            </label>

            <select
              name="discountType"
              className="admin-form-input"
              value={formData.discountType}
              onChange={handleChange}
              required
            >
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </select>
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">
              Discount Value <span className="admin-required">*</span>
            </label>
            <input
              type="number"
              name="discountValue"
              className="admin-form-input"
              value={formData.discountValue}
              onChange={handleChange}
              required
              min="0"
              max={formData.discountType === 'percentage' ? '100' : undefined}
              step={formData.discountType === 'percentage' ? '0.01' : '0.01'}
              placeholder={formData.discountType === 'percentage' ? '10' : '5.00'}
            />
            <span className="admin-form-hint">
              {formData.discountType === 'percentage'
                ? 'Enter percentage (0-100)'
                : 'Enter fixed amount in dollars'}
            </span>
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Minimum Purchase Amount</label>
            <input
              type="number"
              name="minPurchaseAmount"
              className="admin-form-input"
              value={formData.minPurchaseAmount}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>
          {formData.discountType === 'percentage' && (
            <div className="admin-form-group">
              <label className="admin-form-label">Maximum Discount Amount</label>
              <input
                type="number"
                name="maxDiscountAmount"
                className="admin-form-input"
                value={formData.maxDiscountAmount}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="Optional"
              />
              <span className="admin-form-hint">
                Maximum discount amount for percentage discounts
              </span>

            </div>
          )}
          <div className="admin-form-group">
            <label className="admin-form-label">Valid From</label>
            <input
              type="datetime-local"
              name="validFrom"
              className="admin-form-input"
              value={formData.validFrom}
              onChange={handleChange}
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Valid Until</label>
            <input
              type="datetime-local"
              name="validUntil"
              className="admin-form-input"
              value={formData.validUntil}
              onChange={handleChange}
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Maximum Use Count</label>
            <input
              type="number"
              name="maxUseCount"
              className="admin-form-input"
              value={formData.maxUseCount}
              onChange={handleChange}
              min="1"
              placeholder="Unlimited"
            />
            <span className="admin-form-hint">
              Leave empty for unlimited uses
            </span>
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
              />

              Active
            </label>

          </div>


          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="admin-button primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : code ? 'Update' : 'Create'}
            </button>

          </div>

        </form>

      </div>

    </div>

  );
};

export default DiscountCodeForm;

